import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../services/routing/api';
import { useTenantConfigQuery } from '../../services/routing/queries';

const CALLBACKS = [
  {
    key: 'routing_started_url',
    label: 'routing_started_url',
    when: 'before the first route is planned — the platform clears the day on it'
  },
  {
    key: 'route_completed_url',
    label: 'route_completed_url',
    when: 'once per finished route, carrying the rides it planned'
  },
  {
    key: 'routing_finished_url',
    label: 'routing_finished_url',
    when: 'once the day is planned — the platform applies it on this one'
  }
] as const;

type UrlKey = (typeof CALLBACKS)[number]['key'];

const blank: Record<UrlKey, string> = {
  routing_started_url: '',
  route_completed_url: '',
  routing_finished_url: ''
};

/**
 * Blank is valid — it means "fall through to the deployment's". Anything else must be http(s)
 * *with a host*: `http:///callbacks` passes a scheme check and is refused by the server.
 */
const urlValid = (value: string) => {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  // The host has to be there in the text, not merely after parsing: the browser's URL collapses
  // `http:///callbacks` into the host "callbacks", while the server parses with java.net.URI,
  // which sees no host at all and answers 422. Reading it the stricter way is the honest one.
  if (!/^https?:\/\/[^/?#\s]/.test(trimmed)) return false;
  try {
    return !!new URL(trimmed).hostname;
  } catch {
    return false;
  }
};

/**
 * Where this tenant's three run callbacks go, and the one secret their bodies are signed with.
 * Per tenant because a tenant posts to its own dashboard, and each dashboard verifies with its own
 * key. A blank URL falls through to the deployment's, which is what the effective line reports.
 */
const Panel = ({ tenant }: { tenant: string }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, error, refetch } = useTenantConfigQuery(tenant);
  const stored = data?.callbacks;

  const [urls, setUrls] = useState<Record<UrlKey, string>>(blank);
  // Never populated from the server: the secret is write-only, so an empty box means "leave the
  // stored one alone" rather than "there isn't one".
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const hydrated = useRef<string>();

  useEffect(() => {
    setUrls(blank);
    setSecret('');
  }, [tenant]);

  // Filled from the server once per tenant, not on every refetch: saving the secret refetches
  // this row, and re-seeding here would wipe URL edits the operator had typed but not saved.
  useEffect(() => {
    if (!stored || data?.tenant !== tenant || hydrated.current === tenant) return;
    hydrated.current = tenant;
    setUrls({
      routing_started_url: stored.routing_started_url,
      route_completed_url: stored.route_completed_url,
      routing_finished_url: stored.routing_finished_url
    });
  }, [stored, data?.tenant, tenant]);

  // Saving what the form shows before the tenant's own row has been read would write blank URLs
  // over real ones, which is how a tenant silently stops reporting its runs.
  const loaded = !!stored && data?.tenant === tenant;
  const invalid = useMemo(() => CALLBACKS.filter(({ key }) => !urlValid(urls[key])), [urls]);
  const saveable = loaded && invalid.length === 0 && !saving;

  const save = async (body: api.TenantConfigUpdate['callbacks'], message: string) => {
    const saved = tenant;
    setSaving(true);
    try {
      await api.putTenantConfig(saved, { callbacks: body });
      enqueueSnackbar(message);
      // Only this tenant's box: the panel stays mounted across a tenant switch, so a slow save
      // must not clear a replacement secret typed for whoever is on screen now.
      if (saved === tenant) {
        setSecret('');
      }
      await refetch();
    } catch (failure) {
      enqueueSnackbar((failure as Error).message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => {
    if (!saveable) return;
    save(
      {
        ...urls,
        // Omitted rather than sent empty when untouched: "" is what clears the stored secret.
        ...(secret.trim() === '' ? {} : { secret: secret.trim() })
      },
      'Callbacks saved'
    );
  };

  // Two trios that read as configured and quietly do nothing useful. Both are worth saying out
  // loud here, because neither shows up as an error anywhere else: the run succeeds either way.
  const nothingWouldBeStaged =
    loaded && !stored.effective_route_completed_url
    && (!!stored.effective_routing_started_url || !!stored.effective_routing_finished_url);
  const dayNeverCleared =
    loaded && !!stored.effective_routing_finished_url && !stored.effective_routing_started_url;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        PLATFORM CALLBACKS
      </Typography>
      {isError ? (
        <Alert severity="error">
          Could not read the callbacks for &ldquo;{tenant}&rdquo; ({(error as Error).message}).
          Saving is disabled until it loads — otherwise this form would write its blanks over
          whatever is really there.
        </Alert>
      ) : isLoading || !loaded ? (
        <Typography variant="body2" color="text.secondary">
          loading&#8230;
        </Typography>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Where this tenant&apos;s runs report to. Leave a URL blank to fall through to the
            deployment&apos;s. One secret signs all three, because the receiving dashboard verifies
            them against a single key — it is never read back, only replaced.
          </Typography>

          {nothingWouldBeStaged && (
            <Alert severity="warning">
              route_completed_url is unset, so the day callbacks are not made at all: clearing the
              day and applying it without ever saying what to stage would empty it.
            </Alert>
          )}

          {dayNeverCleared && (
            <Alert severity="warning">
              routing_started_url is unset, so the day is applied but never cleared. The first run
              of a day still stages, and every run after it stages nothing: those routes are no
              longer pending, and nothing resets them.
            </Alert>
          )}

          <Stack spacing={2} sx={{ maxWidth: 620 }}>
            {CALLBACKS.map(({ key, label, when }) => {
              const effective = stored[`effective_${key}` as const];
              const fellThrough = urls[key].trim() === '' && !!effective;
              return (
                <TextField
                  key={key}
                  size="small"
                  label={label}
                  value={urls[key]}
                  onChange={event => setUrls(current => ({ ...current, [key]: event.target.value }))}
                  error={!urlValid(urls[key])}
                  inputProps={{ 'aria-label': label }}
                  helperText={
                    !urlValid(urls[key])
                      ? 'must be an http(s) URL, or blank to use the deployment’s'
                      : fellThrough
                        ? `${when} — currently ${effective}`
                        : when
                  }
                />
              );
            })}

            <TextField
              size="small"
              type="password"
              label="secret"
              value={secret}
              onChange={event => setSecret(event.target.value)}
              inputProps={{ 'aria-label': 'secret' }}
              helperText={
                stored.secret_set
                  ? 'a secret is set — type to replace it, or leave blank to keep it'
                  : 'no secret of its own: the callbacks are signed with the deployment’s key, or not at all'
              }
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={onSave} disabled={!saveable}>
              Save callbacks
            </Button>
            {stored.secret_set && (
              <Button
                color="error"
                disabled={saving}
                onClick={() => save({ secret: '' }, 'Secret cleared')}
              >
                Clear the secret
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

/** Memoised on `tenant`, for the reason NightlySchedulePanel is: it is a sibling of the forms. */
export const CallbacksPanel = memo(Panel);

export default CallbacksPanel;
